import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  People,
  Chat,
  AccessTime,
  CheckCircle,
  Star,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { reportsAPI } from '../../services/api';
import { ExportButton } from './ExportButton';

interface AgentPerformanceProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface AgentData {
  agent_id: number;
  agent_name: string;
  conversations_handled: number;
  messages_sent: number;
  avg_response_time: number;
  resolution_count: number;
  csat_score: number;
}

const PerformanceMetric: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, icon, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Box sx={{ 
      backgroundColor: `${color}.light`, 
      borderRadius: 1, 
      p: 1, 
      mr: 2,
      color: `${color}.main`
    }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" color={`${color}.main`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </Box>
  </Box>
);

export const AgentPerformance: React.FC<AgentPerformanceProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentData, setAgentData] = useState<AgentData[]>([]);

  useEffect(() => {
    fetchAgentPerformance();
  }, [dateRange]);

  const fetchAgentPerformance = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await reportsAPI.getAgentReport(params);
      // Backend returns {agents: [...]} so extract the agents array
      setAgentData(data.agents || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch agent performance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!agentData.length) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No agent performance data available for the selected period.
      </Alert>
    );
  }

  // Calculate totals and averages
  const totalConversations = agentData.reduce((sum, agent) => sum + agent.conversations_handled, 0);
  const totalMessages = agentData.reduce((sum, agent) => sum + agent.messages_sent, 0);
  const totalResolutions = agentData.reduce((sum, agent) => sum + agent.resolution_count, 0);
  const avgCSAT = agentData.reduce((sum, agent) => sum + agent.csat_score, 0) / agentData.length;

  // Sort agents by performance
  const sortedAgents = [...agentData].sort((a, b) => b.conversations_handled - a.conversations_handled);
  const topPerformer = sortedAgents[0];

  // Prepare data for export
  const exportData = agentData || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <People sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            Agent Performance Analytics
          </Typography>
        </Box>
        <ExportButton
          data={exportData}
          filename="agent_performance"
          metadata={{ dateRange }}
        />
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <PerformanceMetric
                title="Total Agents"
                value={agentData.length}
                icon={<People />}
                color="primary"
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <PerformanceMetric
                title="Total Conversations"
                value={totalConversations}
                icon={<Chat />}
                color="secondary"
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <PerformanceMetric
                title="Total Resolutions"
                value={totalResolutions}
                icon={<CheckCircle />}
                color="success"
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <PerformanceMetric
                title="Avg CSAT Score"
                value={`${avgCSAT.toFixed(1)}/5.0`}
                icon={<Star />}
                color="warning"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Performer Highlight */}
      {topPerformer && (
        <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🏆 Top Performer: {topPerformer.agent_name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2">Conversations Handled</Typography>
                <Typography variant="h5">{topPerformer.conversations_handled}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2">Messages Sent</Typography>
                <Typography variant="h5">{topPerformer.messages_sent}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2">Resolutions</Typography>
                <Typography variant="h5">{topPerformer.resolution_count}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2">CSAT Score</Typography>
                <Typography variant="h5">{topPerformer.csat_score.toFixed(1)}/5</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Detailed Agent Performance Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Individual Agent Performance
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell>
                  <TableCell align="right">Conversations</TableCell>
                  <TableCell align="right">Messages Sent</TableCell>
                  <TableCell align="right">Avg Response Time</TableCell>
                  <TableCell align="right">Resolutions</TableCell>
                  <TableCell align="right">Resolution Rate</TableCell>
                  <TableCell align="right">CSAT Score</TableCell>
                  <TableCell align="right">Performance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAgents.map((agent, index) => {
                  const resolutionRate = agent.conversations_handled > 0 
                    ? (agent.resolution_count / agent.conversations_handled) * 100 
                    : 0;
                  
                  const isTopPerformer = index === 0;
                  const performanceScore = (agent.conversations_handled * 0.4) + 
                                         (agent.resolution_count * 0.4) + 
                                         (agent.csat_score * 20 * 0.2);
                  
                  return (
                    <TableRow key={agent.agent_id} sx={{ 
                      bgcolor: isTopPerformer ? 'success.light' : 'inherit',
                      opacity: isTopPerformer ? 1 : 0.9
                    }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {agent.agent_name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {agent.agent_name}
                              {isTopPerformer && (
                                <Chip 
                                  label="Top" 
                                  size="small" 
                                  color="success" 
                                  sx={{ ml: 1 }} 
                                />
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {agent.conversations_handled}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{agent.messages_sent}</TableCell>
                      <TableCell align="right">
                        {agent.avg_response_time > 0 ? `${agent.avg_response_time} min` : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {agent.resolution_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Box sx={{ width: 60, mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(resolutionRate, 100)} 
                              color={resolutionRate > 80 ? 'success' : resolutionRate > 60 ? 'warning' : 'error'}
                            />
                          </Box>
                          <Typography variant="body2">
                            {resolutionRate.toFixed(0)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Star sx={{ color: 'warning.main', mr: 0.5, fontSize: 16 }} />
                          <Typography variant="body2">
                            {agent.csat_score.toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={performanceScore > 80 ? 'Excellent' : performanceScore > 60 ? 'Good' : 'Needs Improvement'}
                          color={performanceScore > 80 ? 'success' : performanceScore > 60 ? 'primary' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};